/**
 * SSO Auto-Login for AGS Suite integration.
 *
 * When a user clicks the VoltIQ tool card in the AGS Suite dashboard,
 * the Suite appends these query params to the URL:
 *   - suite_token  : JWT issued by the AGS Suite backend
 *   - login_hint   : user email
 *   - user_name    : display name
 *   - user_role    : role string
 *
 * This module:
 *   1. Detects SSO params on page load
 *   2. Verifies the token against the Suite backend
 *   3. Stores the authenticated user in localStorage
 *   4. Cleans SSO params from the URL
 */

const SSO_USER_KEY = 'voltiq_sso_user';
const SSO_TOKEN_KEY = 'voltiq_suite_token';

/** Persist / read / clear the raw Astra Suite token (sent to our backend). */
export function saveSuiteToken(token) {
  try { localStorage.setItem(SSO_TOKEN_KEY, token); } catch { /* ignore */ }
}
export function getSuiteToken() {
  try { return localStorage.getItem(SSO_TOKEN_KEY) || null; } catch { return null; }
}
export function clearSuiteToken() {
  try { localStorage.removeItem(SSO_TOKEN_KEY); } catch { /* ignore */ }
}

/** Resolve the AGS Suite backend URL (configurable via env). */
function getSuiteApiUrl() {
  const env = process.env.REACT_APP_SUITE_API_URL;
  if (env && env.trim()) {
    return env.trim().replace(/\/+$/, '');
  }
  // Fallback: production URL for deployed builds, localhost for dev
  if (
    typeof window !== 'undefined' &&
    window.location &&
    !/localhost|127\.0\.0\.1/i.test(window.location.origin)
  ) {
    return 'https://ags-suite-api.vercel.app';
  }
  return 'http://localhost:3001';
}

/**
 * Check the current URL for SSO query params.
 * Returns { suiteToken, loginHint, userName, userRole } or null.
 */
export function extractSsoParams() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const suiteToken = params.get('suite_token');
  if (!suiteToken) return null;
  return {
    suiteToken,
    loginHint: params.get('login_hint') || '',
    userName: params.get('user_name') || '',
    userRole: params.get('user_role') || '',
  };
}

/**
 * Remove SSO-related query params from the browser URL without triggering
 * a page reload (uses replaceState).
 */
export function cleanSsoParams() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  ['suite_token', 'login_hint', 'user_name', 'user_role'].forEach((p) =>
    url.searchParams.delete(p)
  );
  // Only replace if something actually changed
  if (url.toString() !== window.location.href) {
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * Verify a suite_token against the AGS Suite backend.
 * Returns user object { id, email, name, role } on success, or null.
 */
export async function verifySsoToken(suiteToken) {
  try {
    const baseUrl = getSuiteApiUrl();
    const res = await fetch(
      `${baseUrl}/api/auth/sso-verify?suite_token=${encodeURIComponent(suiteToken)}`,
      { method: 'GET', headers: { Accept: 'application/json' } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    // The verify endpoint returns { valid, user: { id, email, name, role } }
    const user = data.user || data;
    if (user && user.email) {
      return {
        id: user.id || null,
        email: user.email,
        name: user.name || '',
        role: user.role || '',
      };
    }
    return null;
  } catch (err) {
    console.error('[SSO] Token verification failed:', err);
    return null;
  }
}

/**
 * Full SSO auto-login flow. Call once on app mount.
 * Returns the authenticated user object or null.
 */
export async function handleSsoAutoLogin() {
  const ssoParams = extractSsoParams();
  if (!ssoParams) return null;

  // New SSO token arriving — clear any previous session first
  clearSsoUser();

  // Always clean params from URL regardless of verification outcome
  cleanSsoParams();

  // Keep the raw token so backend calls can be authenticated (X-Suite-Token).
  saveSuiteToken(ssoParams.suiteToken);

  const user = await verifySsoToken(ssoParams.suiteToken);
  if (user) {
    saveSsoUser(user);
    return user;
  }

  // Token invalid — try falling back to the params themselves if login_hint present
  // (graceful degradation: at minimum show the user who they are)
  if (ssoParams.loginHint) {
    const fallbackUser = {
      id: null,
      email: ssoParams.loginHint,
      name: ssoParams.userName || ssoParams.loginHint,
      role: ssoParams.userRole || '',
    };
    saveSsoUser(fallbackUser);
    return fallbackUser;
  }

  return null;
}

/** Persist SSO user to localStorage. */
export function saveSsoUser(user) {
  try {
    localStorage.setItem(SSO_USER_KEY, JSON.stringify(user));
  } catch {
    // storage full or private browsing — ignore
  }
}

/** Retrieve the stored SSO user (or null). */
export function getSsoUser() {
  try {
    const raw = localStorage.getItem(SSO_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Clear the stored SSO user (logout). */
export function clearSsoUser() {
  localStorage.removeItem(SSO_USER_KEY);
  clearSuiteToken();
}
